import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { BadgeCheck, Landmark, Link2, Loader2, Pencil, Search } from 'lucide-react';
import api from '../../utils/api';
import { Dialog } from '../../components/ui';
import { dmy, errText, usePayEngine } from '../Settings/payRatesKit';
import usePayPermissions, { needsPermission } from '../../hooks/usePayPermissions';

// Worker Profile > Pay Rates for a contractor (owner spec Pay Rates v1.2, 5.2): the Xero contact their
// bills go to (picked from Xero's Accounting contacts, stored by ContactID), the GST Registered tick
// (GST is added to their bills when on) and the bank details the ABA file pays, with the SC module's
// checks. The account number is only ever shown masked. Backend: routes/contractorBills.js.

function ContactPicker({ open, onClose, onPick }) {
  const [search, setSearch] = useState('');
  const [list, setList] = useState(null);
  const [error, setError] = useState('');
  const find = useCallback(async (term) => {
    setError('');
    try { const { data } = await api.get('/contractor-bills/xero/contacts', { params: term ? { search: term } : {} }); setList(data.contacts || []); }
    catch (e) { setList([]); setError(errText(e, 'Could not read your Xero contacts.')); }
  }, []);
  useEffect(() => { if (open) { setSearch(''); setList(null); find(''); } }, [open, find]);
  return (
    <Dialog open={open} onClose={onClose} size="md" title="Choose the Xero contact" description="Bills for this contractor go to this contact in Xero.">
      <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); setList(null); find(search.trim()); }}>
        <label htmlFor="cpp-search" className="sr-only">Search Xero contacts</label>
        <input id="cpp-search" className="input-field flex-1" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name or email" />
        <button type="submit" className="btn-secondary"><Search size={14} aria-hidden="true" />Search</button>
      </form>
      {error && <p className="mt-3 text-[13px] text-rose-700">{error}</p>}
      {list === null ? <p className="mt-3 flex items-center gap-2 text-[13px] text-slate-500"><Loader2 size={14} className="animate-spin" aria-hidden="true" />Reading Xero</p>
        : list.length === 0 ? <p className="mt-3 text-[13px] text-slate-500">No matching contacts. Add the contractor as a contact in Xero first.</p>
          : (
            <ul className="mt-3 max-h-80 divide-y divide-slate-100 overflow-y-auto rounded-lg border border-slate-200">
              {list.map((c) => (
                <li key={c.id}>
                  <button type="button" className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-slate-50" onClick={() => onPick(c)}>
                    <span><span className="font-medium text-slate-900">{c.name}</span>{c.email && <span className="block text-[12px] text-slate-500">{c.email}</span>}</span>
                    {c.is_supplier && <span className="badge-gray">Supplier</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
    </Dialog>
  );
}

function BankDialog({ open, bank, onClose, onSave }) {
  const [f, setF] = useState({ account_name: '', bsb: '', account_number: '', lodgement_ref: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (open) { setF({ account_name: (bank && bank.account_name) || '', bsb: (bank && bank.bsb) || '', account_number: '', lodgement_ref: (bank && bank.lodgement_ref) || '' }); setError(''); }
  }, [open, bank]);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try { await onSave(f); onClose(); } catch (err) { setError(errText(err, 'Could not save the bank details.')); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onClose={() => !busy && onClose()} size="md" title="Bank details" description="The account the ABA payment file pays. Another admin confirms a change before the next payment.">
      <form onSubmit={save} className="space-y-3">
        <div>
          <label htmlFor="cpp-acname" className="input-label">Account name</label>
          <input id="cpp-acname" className="input-field" maxLength={32} value={f.account_name} onChange={(e) => setF({ ...f, account_name: e.target.value })} autoComplete="off" />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="cpp-bsb" className="input-label">BSB</label>
            <input id="cpp-bsb" className="input-field tabular-nums" inputMode="numeric" maxLength={7} placeholder="000-000" value={f.bsb} onChange={(e) => setF({ ...f, bsb: e.target.value })} autoComplete="off" />
          </div>
          <div>
            <label htmlFor="cpp-acno" className="input-label">Account number</label>
            <input id="cpp-acno" className="input-field tabular-nums" inputMode="numeric" maxLength={11} value={f.account_number} onChange={(e) => setF({ ...f, account_number: e.target.value })}
              placeholder={bank ? `Leave blank to keep ${bank.account_masked}` : 'Up to 9 digits'} autoComplete="off" />
          </div>
        </div>
        <div>
          <label htmlFor="cpp-ref" className="input-label">Lodgement reference (optional)</label>
          <input id="cpp-ref" className="input-field" maxLength={18} value={f.lodgement_ref} onChange={(e) => setF({ ...f, lodgement_ref: e.target.value })} placeholder="The bill number is used when blank" />
        </div>
        {error && <p className="text-[13px] text-rose-700" role="alert">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={busy}>{busy && <Loader2 size={14} className="animate-spin" aria-hidden="true" />}Save bank details</button>
        </div>
      </form>
    </Dialog>
  );
}

export default function ContractorPayProfile({ userId }) {
  const engine = usePayEngine();
  const { can } = usePayPermissions();
  const canEdit = can('pay_rates.edit');
  const canConfirm = can('payroll.send_xero');
  const [p, setP] = useState(null);
  const [failed, setFailed] = useState(false);
  const [picking, setPicking] = useState(false);
  const [editBank, setEditBank] = useState(false);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setFailed(false);
    try { const { data } = await api.get(`/contractor-bills/profiles/${userId}`); setP(data); setEmail(data.remittance_email || ''); } catch { setFailed(true); }
  }, [userId]);
  useEffect(() => { if (engine === 'rates' && canEdit) load(); }, [engine, canEdit, load]);
  if (engine !== 'rates' || !canEdit) return null;

  const save = async (body, what) => {
    setBusy(what);
    try { const { data } = await api.put(`/contractor-bills/profiles/${userId}`, body); setP(data); setEmail(data.remittance_email || ''); toast.success('Saved'); return true; }
    catch (e) { toast.error(errText(e, 'Could not save.')); return false; } finally { setBusy(''); }
  };
  const saveBank = async (f) => {
    const { data } = await api.put(`/contractor-bills/profiles/${userId}/bank`, f);
    setP(data.profile);
    toast.success(data.changed ? 'Bank details saved. Another admin confirms them before the next payment.' : 'No change');
  };
  const confirmBank = async () => {
    setBusy('confirm');
    try { const { data } = await api.post(`/contractor-bills/profiles/${userId}/bank/confirm`); setP(data.profile); toast.success('Bank details confirmed'); }
    catch (e) { toast.error(errText(e, 'Could not confirm the bank details.')); } finally { setBusy(''); }
  };

  const bank = p && p.bank;
  return (
    <section aria-label="Contractor bills" className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <header className="flex items-center gap-3 border-b border-slate-100 px-5 py-3.5">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-50 text-violet-700"><Landmark size={15} aria-hidden="true" /></span>
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Xero bills and bank details</h3>
          <p className="text-[12px] text-slate-500">This contractor is paid by Xero bill and ABA file.</p>
        </div>
      </header>
      {failed ? (
        <div className="flex items-center gap-3 px-5 py-4 text-[13px] text-slate-600">We couldn&apos;t load these details.<button type="button" className="btn-ghost btn-sm" onClick={load}>Try again</button></div>
      ) : !p ? (
        <p className="flex items-center gap-2 px-5 py-4 text-[13px] text-slate-500"><Loader2 size={14} className="animate-spin" aria-hidden="true" />Loading</p>
      ) : (
        <dl className="divide-y divide-slate-100 text-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
            <div>
              <dt className="text-[12px] font-medium text-slate-500">Xero contact</dt>
              <dd className="mt-0.5 font-medium text-slate-900">{p.xero_contact_name || <span className="text-amber-800">Not linked. Bills can&apos;t be sent until it is.</span>}</dd>
            </div>
            <div className="flex gap-2">
              {p.xero_contact_id && <button type="button" className="btn-ghost btn-sm" disabled={busy === 'contact'} onClick={() => save({ xero_contact_id: null }, 'contact')}>Unlink</button>}
              <button type="button" className="btn-secondary btn-sm" onClick={() => setPicking(true)}><Link2 size={13} aria-hidden="true" />{p.xero_contact_id ? 'Change' : 'Link contact'}</button>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
            <div>
              <dt className="text-[12px] font-medium text-slate-500">GST Registered</dt>
              <dd className="mt-0.5 text-[12.5px] text-slate-600">{p.gst_registered ? 'GST is added to this contractor\'s bills.' : 'No GST on this contractor\'s bills.'}{!p.abn && ' Add an ABN on the profile to tick it.'}</dd>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" className="rounded border-slate-300 text-primary-600" checked={p.gst_registered} disabled={busy === 'gst'}
                onChange={(e) => save({ gst_registered: e.target.checked }, 'gst')} />
              GST Registered
            </label>
          </div>
          <div className="flex flex-wrap items-end justify-between gap-3 px-5 py-3">
            <div className="min-w-[220px] flex-1">
              <dt><label htmlFor="cpp-email" className="text-[12px] font-medium text-slate-500">Remittance email</label></dt>
              <dd className="mt-1"><input id="cpp-email" type="email" className="input-field" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Their profile email is used when blank" /></dd>
            </div>
            <button type="button" className="btn-secondary btn-sm" disabled={busy === 'email' || email === (p.remittance_email || '')} onClick={() => save({ remittance_email: email }, 'email')}>Save email</button>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
            <div>
              <dt className="text-[12px] font-medium text-slate-500">Bank details</dt>
              <dd className="mt-0.5">
                {bank ? (
                  <span className="tabular-nums text-slate-900">{bank.account_name} · {bank.bsb} {bank.account_masked}
                    {bank.needs_confirmation
                      ? <span className="ml-2 badge-yellow">Changed {dmy(bank.changed_at)}, to confirm</span>
                      : bank.confirmed_at ? <span className="ml-2 badge-green">Confirmed {dmy(bank.confirmed_at)}</span> : null}
                  </span>
                ) : <span className="text-amber-800">None. The ABA file can&apos;t pay this contractor until they are added.</span>}
              </dd>
            </div>
            <div className="flex gap-2">
              {bank && bank.needs_confirmation && (
                <button type="button" className="btn-secondary btn-sm" disabled={!canConfirm || busy === 'confirm'} title={canConfirm ? undefined : needsPermission('payroll.send_xero')} onClick={confirmBank}>
                  <BadgeCheck size={13} aria-hidden="true" />Confirm
                </button>
              )}
              <button type="button" className="btn-secondary btn-sm" onClick={() => setEditBank(true)}><Pencil size={13} aria-hidden="true" />{bank ? 'Change' : 'Add bank details'}</button>
            </div>
          </div>
        </dl>
      )}
      <ContactPicker open={picking} onClose={() => setPicking(false)} onPick={async (c) => { if (await save({ xero_contact_id: c.id }, 'contact')) setPicking(false); }} />
      <BankDialog open={editBank} bank={bank} onClose={() => setEditBank(false)} onSave={saveBank} />
    </section>
  );
}
